"use client";

import { motion, TargetAndTransition, Transition } from "motion/react";
import { AnimatePresence } from "framer-motion";
import { TextMorph } from "@/components/ui/text-morph";
import Image from "next/image";
import { useEffect, useState } from "react";
import { AnimateNumber } from "motion-plus/react";
import { GradientOverlay } from "../UI/GradientOverlay";
import Link from "next/link";
import { menuList } from "@/components/Header/customerList";
import { adjustTracking } from "@/services/adjust";
import { useDownloadContext } from "@/contexts/DownloadContext";

type InfoVariant = "step" | "search" | "browser" | "computer" | "controllable" | "memory" | "menu" | "download";

interface InfoOverlayProps {
  close: () => void;
  size: { width: number; height: number };
  still?: string;
  fellou?: string;
  variant?: InfoVariant;
}

export function InfoOverlay({ close, size, still = "", fellou = "", variant = "step" }: InfoOverlayProps) {
  const [active, setActive] = useState(false);
  const { downloadInfo, isLoading, getMacArmResource, getMacIntelResource, getWindowsResource } = useDownloadContext();

  useEffect(() => {
    const TRIGGER_DELAY_MS = 900; // slightly longer delay
    const t = setTimeout(() => setActive(true), TRIGGER_DELAY_MS);
    return () => clearTimeout(t);
  }, []);
  const transition: Transition = {
    duration: 0.35,
    ease: [0.59, 0, 0.35, 1],
  };

  const enteringState: TargetAndTransition = {
    rotateX: 0,
    skewY: 0,
    scaleY: 1,
    scaleX: 1,
    y: 0,
    transition: {
      ...transition,
      y: { type: "spring", visualDuration: 0.7, bounce: 0.2 },
    },
  };

  const exitingState = {
    rotateX: -5,
    skewY: -1.5,
    scaleY: 2,
    scaleX: 0.4,
    y: 100,
  };

  // Download handling functions
  const downloadFile = (url: string, platform: string) => {
    const link = document.createElement("a");
    link.href = url;
    link.download = url.split("/").pop() || "download";
    link.click();
    const channel = localStorage.getItem("utm_source");

    // 追踪下载
    fetch("https://admin.fellou.ai/api/download/track", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ platform, channel }),
    }).catch((error) => console.error("Error tracking download:", error));

    close();
  };

  // Get resources for UI
  const macArmResource = getMacArmResource();
  const macIntelResource = getMacIntelResource();
  const windowsResource = getWindowsResource();

  // Get info objects for enabled states
  const macInfo = downloadInfo.find((item) => item.type === "mac");
  const windowsInfo = downloadInfo.find((item) => item.type === "windows");

  const handleMacArmDownload = () => {
    // Check if data is loading
    if (isLoading) {
      console.log("Download data still loading, please wait...");
      return;
    }

    const macArmResource = getMacArmResource();
    if (!macArmResource) {
      console.error("Mac ARM resource not available");
      return;
    }

    // Track download events
    adjustTracking.trackDownload({
      platform: "mac_arm",
      source: "info-overlay",
      timestamp: new Date().toISOString(),
    });

    adjustTracking.trackDownloadOnly({
      platform: "mac_arm",
      source: "info-overlay",
      timestamp: new Date().toISOString(),
    });

    adjustTracking.trackMacAppleDownload({
      platform: "mac_arm",
      source: "info-overlay",
      timestamp: new Date().toISOString(),
    });

    downloadFile(macArmResource.url, "mac_arm");
  };

  const handleMacIntelDownload = () => {
    // Check if data is loading
    if (isLoading) {
      console.log("Download data still loading, please wait...");
      return;
    }

    const macIntelResource = getMacIntelResource();
    if (!macIntelResource) {
      console.error("Mac Intel resource not available");
      return;
    }

    // Track download events
    adjustTracking.trackDownload({
      platform: "mac_intel",
      source: "info-overlay",
      timestamp: new Date().toISOString(),
    });

    adjustTracking.trackDownloadOnly({
      platform: "mac_intel",
      source: "info-overlay",
      timestamp: new Date().toISOString(),
    });

    adjustTracking.trackMacIntelDownload({
      platform: "mac_intel",
      source: "info-overlay",
      timestamp: new Date().toISOString(),
    });

    downloadFile(macIntelResource.url, "mac_intel");
  };

  const handleWindowsDownload = () => {
    // Check if data is loading
    if (isLoading) {
      console.log("Download data still loading, please wait...");
      return;
    }

    const windowsResource = getWindowsResource();
    if (!windowsResource) {
      console.error("Windows resource not available");
      return;
    }

    // Track download events
    adjustTracking.trackDownload({
      platform: "windows",
      source: "info-overlay",
      timestamp: new Date().toISOString(),
    });

    adjustTracking.trackDownloadOnly({
      platform: "windows",
      source: "info-overlay",
      timestamp: new Date().toISOString(),
    });

    adjustTracking.trackWindowsDownload({
      platform: "windows",
      source: "info-overlay",
      timestamp: new Date().toISOString(),
    });

    downloadFile(windowsResource.url, "windows");
  };

  return (
    <div className="overlay-root" onClick={close}>
      <GradientOverlay size={size} variant="subtle" />
      <motion.div
        className="overlay-content"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={transition}
      >
        <motion.div
          className="download-confirmation"
          onClick={(e) => e.stopPropagation()}
          initial={exitingState}
          animate={enteringState}
          exit={exitingState}
          transition={transition}
          style={{
            transformPerspective: 1000,
            originX: 0.5,
            originY: 0,
          }}
        >
          <header>
            {/* Still line with small clever effects, auto-activated after 0.5s */}
            {variant === "menu" && (
              <div className="text-center">
                <h2 className="h3 text-2xl font-semibold">Menu</h2>
                <p className="big">Explore Fellou</p>
              </div>
            )}
            {variant === "step" && (
              <div className="text-center">
                <h2 className="h3 text-2xl font-semibold">
                  Still typing <br /> <TextMorph className="inline">{active ? "loooooooong" : "long"}</TextMorph>{" "}
                  prompts?
                </h2>
              </div>
            )}
            {variant === "search" && (
              <div className="text-center">
                <h2 className="h3 text-2xl font-semibold">
                  Still opening{" "}
                  <AnimateNumber className="inline" style={{ fontVariantNumeric: "tabular-nums" }}>
                    {active ? 300 : 10}
                  </AnimateNumber>{" "}
                  tabs?
                </h2>
              </div>
            )}
            {variant === "browser" && (
              <div className="text-center relative">
                <AnimatePresence>
                  {active && (
                    <motion.div
                      className="absolute -top-28 left-1/2 -translate-x-1/2"
                      initial={{ opacity: 0, y: -20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20, transition: { duration: 0.2 } }}
                      transition={{ duration: 0.5 }}
                    >
                      <img
                        src="https://dev-to-uploads.s3.amazonaws.com/uploads/articles/km2w1ppw3yw9pd9na7mu.gif"
                        alt="Fellou in action"
                        className="w-40 h-auto border-2 border-white rounded-lg"
                      />
                    </motion.div>
                  )}
                </AnimatePresence>
                <h2 className="h3 text-2xl font-semibold">
                  Still clicking through <br /> the web yourself?
                </h2>
              </div>
            )}
            {variant === "computer" && (
              <div className="text-center">
                <h2 className="h3 text-2xl font-semibold">
                  Still switching apps <br /> on AI's orders?
                </h2>
                <AnimatePresence>
                  {active && (
                    <motion.div
                      className="mt-3 flex items-center justify-center"
                      initial={{ opacity: 0, scale: 0.8, y: 10 }}
                      animate={{ opacity: 1, scale: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.8, y: 10, transition: { duration: 0.2 } }}
                      transition={{ type: "spring", stiffness: 300, damping: 25 }}
                    >
                      {[
                        { src: "https://api.iconify.design/logos:slack-icon.svg", alt: "Slack" },
                        { src: "https://api.iconify.design/logos:notion-icon.svg", alt: "Notion" },
                        { src: "https://api.iconify.design/logos:visual-studio-code.svg", alt: "VS Code" },
                      ].map((icon, index) => (
                        <motion.img
                          key={`${icon.src}-${index}`}
                          src={icon.src}
                          alt={icon.alt}
                          className="w-10 h-10 rounded-lg bg-white/10 backdrop-blur-sm border border-white/20 shadow-md mx-1 p-1"
                          initial={{ opacity: 0, scale: 0.6, y: index % 2 === 0 ? -6 : 6 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ delay: index * 0.08, type: "spring", stiffness: 300, damping: 25 }}
                        />
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )}
            {variant === "controllable" && (
              <div className="text-center">
                <h2 className="h3 text-2xl font-semibold">Still can't step in when AI messes up?</h2>
              </div>
            )}
            {variant === "memory" && (
              <div className="text-center">
                <h2 className="h3 text-2xl font-semibold">Still manually feeding context to AI?</h2>
              </div>
            )}
            {variant === "download" && (
              <div className="text-center">
                <h2 className="h3 text-2xl font-semibold">Download Fellou</h2>
                <p className="big">Choose your platform to start browsing with an AI assistant</p>
              </div>
            )}

            {variant !== "menu" && variant !== "download" && (
              <div className="mt-2 flex items-center justify-center gap-2">
                <AnimatePresence mode="popLayout">
                  {active && (
                    <motion.div
                      className="relative flex-shrink-0 size-8"
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20, transition: { duration: 0.2 } }}
                      transition={{ duration: 0.5 }}
                    >
                      <Image
                        src="/favicon.ico"
                        alt="fellou"
                        fill
                        priority
                        unoptimized
                        className="rounded p-0.5 ring-1 ring-white/20"
                      />
                    </motion.div>
                  )}
                </AnimatePresence>
                <motion.div className="big text-lg font-bold" layout>
                  {fellou}
                </motion.div>
              </div>
            )}
          </header>

          {variant === "menu" && (
            <div className="menu-list">
              {["use-cases", "pricing", "agent-framework", "blog", "join-us"].map((key) => {
                const item = (menuList as any[]).find((i) => i.key === key);
                if (!item) return null;
                const href = item.href || "#";
                const isExternal = !!href && !href.startsWith("/");
                const content = <span className="menu-title">{item.title}</span>;
                return isExternal ? (
                  <a
                    key={key}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="menu-btn"
                    onClick={close}
                  >
                    {content}
                  </a>
                ) : (
                  <Link key={key} href={href} className="menu-btn" onClick={close}>
                    {content}
                  </Link>
                );
              })}
            </div>
          )}
          <div className="controls">
            {variant === "download" && (
              <>
                <button
                  onClick={handleMacArmDownload}
                  className={`platform-btn ${!macInfo?.isEnabled || !macArmResource ? "platform-btn-disabled" : ""}`}
                  disabled={!macInfo?.isEnabled || !macArmResource}
                >
                  <img
                    src="https://api.iconify.design/logos:apple.svg"
                    alt="Apple"
                    className={`platform-icon ${!macInfo?.isEnabled || !macArmResource ? "opacity-50" : ""}`}
                  />
                  {macArmResource?.text || "macOS (Apple Silicon)"}
                </button>
                <button
                  onClick={handleMacIntelDownload}
                  className={`platform-btn ${!macInfo?.isEnabled || !macIntelResource ? "platform-btn-disabled" : ""}`}
                  disabled={!macInfo?.isEnabled || !macIntelResource}
                >
                  <img
                    src="https://api.iconify.design/logos:apple.svg"
                    alt="Apple"
                    className={`platform-icon ${!macInfo?.isEnabled || !macIntelResource ? "opacity-50" : ""}`}
                  />
                  {macIntelResource?.text || "macOS (Intel)"}
                </button>
                <button
                  onClick={handleWindowsDownload}
                  // disabled={!windowsInfo?.isEnabled || !windowsResource}
                  className={`platform-btn `}
                >
                  <img
                    src="https://api.iconify.design/logos:microsoft-icon.svg"
                    alt="Microsoft"
                    className={`platform-icon `}
                  />
                  <div className="flex items-center justify-between w-full">
                    <span>{windowsResource?.overlayTitle || "Windows"}</span>
                  </div>
                </button>
              </>
            )}
            <button onClick={close} className="cancel">
              Close
            </button>
          </div>
        </motion.div>
      </motion.div>

      <style jsx global>{`
        .overlay-root {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          /* Ensure consistent mobile viewport sizing */
          height: 100dvh;
          width: 100vw;
          overflow: hidden;
          z-index: 1000;
        }

        .overlay-content {
          background: rgb(147, 51, 234, 0.2);
          backdrop-filter: blur(3px);
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          /* Normalize height to dynamic viewport */
          height: 100%;
          min-height: 100dvh;
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1001;
          will-change: opacity;
        }

        .download-confirmation {
          width: 88%;
          max-width: 720px;
          position: relative;
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          gap: 30px;
          will-change: transform;
          text-align: center;
          /* Prevent content from making the overlay appear taller */
          max-height: calc(100dvh - 64px);
          overflow-y: auto;
        }

        .download-confirmation header {
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          gap: 5px;
          text-align: center;
        }

        .download-confirmation h2 {
          color: #f5f5f5;
          margin: 0;
        }

        .download-confirmation p {
          color: #f5f5f5;
          margin: 0;
        }

        .controls {
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          gap: 10px;
        }

        .menu-list {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 10px;
        }

        .menu-btn {
          background-color: #f5f5f5;
          color: #0f1115;
          border-radius: 14px;
          padding: 12px 20px;
          border: none;
          font-size: 16px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
          width: 260px;
          text-align: center;
        }

        .menu-btn:hover {
          background-color: #e8e8e8;
        }

        button.cancel {
          background: none;
          color: #f5f5f5;
          border: none;
          padding: 10px 20px;
          font-size: 16px;
          cursor: pointer;
          transition: all 0.2s;
        }

        button.cancel:hover {
          opacity: 0.7;
        }

        button.platform-btn {
          background-color: #f5f5f5;
          color: #0f1115;
          border-radius: 20px;
          padding: 12px 24px;
          border: none;
          font-size: 16px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
          width: 300px;
          display: flex;
          align-items: center;
          gap: 12px;
          justify-content: flex-start;
        }

        button.platform-btn:hover:not(:disabled) {
          background-color: #e0e0e0;
        }

        button.platform-btn-disabled {
          background-color: #f0f0f0;
          color: #999;
          cursor: not-allowed;
        }

        button.platform-btn-disabled:hover {
          background-color: #f0f0f0;
        }

        .platform-icon {
          width: 20px;
          height: 20px;
          flex-shrink: 0;
        }

        .gradient-container {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          z-index: 1000;
          pointer-events: none;
        }

        .expanding-circle {
          position: absolute;
          border-radius: 50%;
          background: rgb(196, 181, 253, 0.8);
          filter: blur(15px);
          transform-origin: center;
          will-change: transform;
        }

        .gradient-circle {
          position: absolute;
          border-radius: 50%;
          filter: blur(100px);
          aspect-ratio: 1;
          will-change: transform;
        }

        .top-left {
          background: rgb(147, 51, 234, 0.9);
        }

        .bottom-right {
          background: rgb(168, 85, 247, 0.9);
        }

        @media (min-aspect-ratio: 1/1) {
          .gradient-circle {
            filter: blur(120px);
          }

          .top-left {
            background: rgb(147, 51, 234, 0.8);
          }

          .bottom-right {
            background: rgb(168, 85, 247, 0.8);
          }
        }
      `}</style>
    </div>
  );
}
